import { CSSProperties, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DiscountsEnded, PercentOff, UpgradeQuote, cancelPlan, createCheckout, createPortalSession, downgrade, getBilling,
  getDowngradeCheck, getPlansCatalog, quoteUpgrade, upgradePlan,
} from '../../api/admin';
import { ApiRequestError } from '../../api/client';
import { DowngradeCheck, DowngradeCheckItem, PlanLimits, TenantPlan } from '../../shared/types';
import { annualSavingCents, formatStorage, longDate, money, moneyExact } from '../platform/planDisplay';
import {
  AI_ASSISTANT_LINE, PLAN_FEATURE_LINE, PLAN_FEATURE_ORDER, aiAllowance, assistantAllowance, assistantLine, featureLine,
} from '../../shared/planFeatureLines';
import { Modal, TableCard } from '../../components/fs';
import { PHONE, useMediaQuery } from '../../shared/media';
import { cardLine } from './BillingSettings';

/**
 * The plan picker (R11, screen 34): the plan cards, the side-by-side table,
 * and the downgrade checklist, inside the settings frame with Billing & plan
 * lit in the sidebar.
 *
 * Cards are generated from the plans table, so a retired or private plan
 * simply is not here. The current plan's button says "Keep …", never a
 * greyed-out "Current plan" — during a trial the current plan is the one
 * about to be charged for, and keeping it is a real choice.
 *
 * What a click does is said on the card before it is clicked (D7). One
 * subscription per account (Ben, 2026-09-25): a FIRST paid plan goes to
 * Stripe's checkout, with the free days while the account still has them;
 * an account that already pays moves up in place — a dialog quotes Stripe's
 * own prorated charge and the card on file, and nothing moves until it is
 * confirmed. A downgrade goes through the gate and changes at the renewal
 * ("Changes on 1 October 2026").
 *
 * The downgrade gate re-checks on load and on window focus — disabling a
 * form or deleting files elsewhere and coming back re-runs it with no page
 * reload. The enabled button is a courtesy; the server re-checks anyway.
 */
export default function PlanPicker() {
  const [plans, setPlans] = useState<TenantPlan[] | null>(null);
  const [currentPlanId, setCurrentPlanId] = useState<number | null>(null);
  const [trialKey, setTrialKey] = useState<string | null>(null);
  const [trialEnds, setTrialEnds] = useState<string | null>(null);
  /**
   * The subscription as the Billing payload mirrors it: when a downgrade takes
   * effect (the renewal, or now when there is nothing to renew), and which
   * kind of upgrade a click is.
   */
  const [renewal, setRenewal] = useState<{
    at: string | null; subscribed: boolean; status: string | null; interval: 'month' | 'year';
  } | null>(null);
  /** The free days a first paid plan still starts with — once per account. */
  const [trialOffer, setTrialOffer] = useState<{ days: number } | null>(null);
  /**
   * The discount the account has now. A plan change ends the agreed price and
   * the percent-off the moment it is made (Ben, 2026-09-26), so a downgrade
   * card says so before the click; the upgrade dialog hears it from the quote.
   */
  const [discounts, setDiscounts] = useState<{ agreed: boolean; percent: PercentOff | null }>({ agreed: false, percent: null });
  /** The plan whose upgrade dialog is open. */
  const [upgrading, setUpgrading] = useState<TenantPlan | null>(null);
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [check, setCheck] = useState<DowngradeCheck | null>(null);
  const [gateKey, setGateKey] = useState<string | null>(null);
  const [checkoutErr, setCheckoutErr] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [downNote, setDownNote] = useState('');
  const [error, setError] = useState('');
  const gateRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  /**
   * On a phone five stacked cards are five screens, so only one is open —
   * the plan you are on, until somebody taps another (§4G G6). `null` before
   * the catalog loads means "the current one", resolved below.
   */
  const phone = useMediaQuery(PHONE);
  const [openPlan, setOpenPlan] = useState<string | null>(null);

  useEffect(() => {
    getPlansCatalog()
      .then((r) => { setPlans(r.plans); setCurrentPlanId(r.currentPlanId); })
      .catch((e) => setError(e.message));
    // The active free period decides which card carries "Your trial" and
    // which button reads "Keep …"; the renewal date is when a downgrade lands.
    // Not fatal if it cannot be read.
    getBilling().then((b) => {
      setTrialKey(b.trial?.planKey ?? null);
      setTrialEnds(b.trial?.expiresAt ?? null);
      setRenewal({
        at: b.subscription?.currentPeriodEnd ?? null,
        subscribed: !!b.subscription?.hasSubscription,
        status: b.subscription?.status ?? null,
        interval: b.subscription?.billingInterval === 'year' ? 'year' : 'month',
      });
      setTrialOffer(b.trialOffer ?? null);
      setDiscounts({ agreed: !!b.price?.overridden, percent: b.percentOff ?? null });
    }).catch(() => undefined);
  }, []);

  const current = plans?.find((p) => p.id === currentPlanId) ?? null;
  const effectiveKey = trialKey ?? current?.key ?? null;
  const shownPlan = openPlan ?? effectiveKey;
  const effective = plans?.find((p) => p.key === effectiveKey) ?? current;
  const below = plans && effective
    ? plans.filter((p) => p.sort_order < effective.sort_order)
    : [];
  const targetKey = gateKey ?? (below.length ? below[0].key : null);
  const gatePlan = plans?.find((p) => p.key === targetKey) ?? null;

  const runCheck = useCallback(() => {
    if (!targetKey) return;
    getDowngradeCheck(targetKey).then((r) => setCheck(r.check)).catch(() => undefined);
  }, [targetKey]);

  useEffect(() => {
    runCheck();
    window.addEventListener('focus', runCheck);
    return () => window.removeEventListener('focus', runCheck);
  }, [runCheck]);

  // The annual discount is a field on the plan; every derived string is
  // computed from it, never typed. "Up to" because the discount is per-plan,
  // and the wording is the public pricing page's — the two toggles say the
  // same thing on purpose.
  const pct = Math.max(0, ...(plans ?? [])
    .filter((p) => p.price_monthly_cents !== null)
    .map((p) => p.annual_discount_pct));
  const annualLabel = pct > 0 ? `Annual · Save up to ${pct}%` : 'Annual';

  /** "Changes on 1 October 2026", or today when there is nothing to renew. */
  const changesWhen = renewal && renewal.subscribed && renewal.at
    ? `Changes on ${longDate(renewal.at)}`
    : renewal ? 'Changes today' : 'Changes at your renewal';

  /**
   * What an upgrade click does, said on the card before it is clicked (D7):
   * Stripe's checkout for a first paid plan, otherwise which of the four
   * in-place moves it is.
   */
  function upgradeNote(p: TenantPlan): string {
    if (!renewal?.subscribed) {
      if (!trialOffer) return 'You’ll confirm payment on the next screen.';
      const cents = interval === 'year' ? p.price_annual_cents : p.price_monthly_cents;
      return `${trialOffer.days} days free, then ${money(cents, p.currency)}/${interval === 'year' ? 'yr' : 'mo'}. Card needed today.`;
    }
    if (trialKey) return 'Ends your free period — billed in full today.';
    if (renewal.status === 'trialing') {
      return renewal.at ? `Still free until ${longDate(renewal.at)}, then this plan’s price.` : 'Still free until your trial ends.';
    }
    if (renewal.interval === 'month' && interval === 'year') return 'Your year starts today, less what’s left of this month.';
    if (renewal.interval === 'year' && interval === 'month') return 'Prorated today on annual; monthly from your renewal.';
    return 'Prorated today — your billing date stays.';
  }

  async function onUpgrade(p: TenantPlan) {
    setCheckoutErr('');
    // Already paying: the one subscription moves in place, after a quote.
    if (renewal?.subscribed) { setUpgrading(p); return; }
    setBusyKey(p.key);
    try {
      const { url } = await createCheckout(p.key, interval);
      window.location.assign(url);
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 409 && (e.body as { code?: string } | undefined)?.code === 'has_subscription') {
        // The page had not heard about the subscription yet; the server had.
        setUpgrading(p);
      } else {
        setCheckoutErr(e instanceof ApiRequestError && e.status === 503
          ? "Stripe isn't connected on this install yet — upgrades are off until it is."
          : (e as Error).message);
      }
    }
    setBusyKey(null);
  }

  async function onDowngrade(key: string) {
    setBusyKey(key);
    setDownNote('');
    try {
      const r = key === 'free' ? await cancelPlan() : await downgrade(key);
      if (!r.ok) {
        // Refused with the same payload the gate renders — show it, live.
        setGateKey(key);
        setCheck(r.check);
        gateRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' });
      } else {
        setDownNote([
          r.scheduled && r.effectiveAt
            ? `Scheduled — you move to ${gatePlanName(plans, key)} on ${longDate(r.effectiveAt)}. Nothing is deleted.`
            : `Done — you are on ${gatePlanName(plans, key)}. Nothing was deleted.`,
          ...endedWords(r.discountsEnded, current?.currency),
        ].join(' '));
        // They are gone now; the cards stop saying they will go. Only when
        // something did end: going back to their own plan ends nothing.
        const ended = r.discountsEnded;
        if (ended && (ended.agreedCents !== null || ended.percent)) setDiscounts({ agreed: false, percent: null });
      }
    } catch (e) { setError((e as Error).message); }
    setBusyKey(null);
  }

  if (error) return <div className="ad-err">{error}</div>;
  if (!plans || !effective) return <div className="fs-card pad st-col">Loading…</div>;

  // What a downgrade ends, said on the card and the checklist before the click.
  const downgradeEnds = [
    discounts.agreed ? 'Your agreed price ends when you make this change.' : '',
    discounts.percent ? `Your ${discounts.percent.percent}% off ends when you make this change.` : '',
  ].filter(Boolean);
  // …except going back to the plan they already pay for, which a free period
  // shows below the trialled plan: the same plan at the same interval is no
  // plan change, and the server ends nothing for it.
  const endsFor = (p: TenantPlan): string[] => (p.id === currentPlanId ? [] : downgradeEnds);

  return (
    <div className="st-stack bp-picker">
      <Link className="st-crumb" to="/admin/settings/billing">← Billing</Link>

      <div className="fs-page-head">
        <div className="text">
          <h1>Choose a plan</h1>
          <div className="fs-page-sub">
            {interval === 'month'
              ? (pct > 0 ? `Monthly · switch to annual to save up to ${pct}%` : 'Monthly')
              : 'Annual · one charge a year, up front, at each plan’s annual price'}
          </div>
        </div>
        <div className="fs-seg bp-interval" role="group" aria-label="Billing period">
          <button type="button" className={interval === 'month' ? 'on' : ''} aria-pressed={interval === 'month'}
            onClick={() => setInterval('month')}>Monthly</button>
          <button type="button" className={interval === 'year' ? 'on' : ''} aria-pressed={interval === 'year'}
            onClick={() => setInterval('year')}>{annualLabel}</button>
        </div>
      </div>

      {checkoutErr && <div className="bl-banner bl-amber" role="alert"><span className="bl-banner-text">{checkoutErr}</span></div>}
      {downNote && <div className="bl-banner bl-ok" role="status"><span className="bl-banner-text">{downNote}</span></div>}

      {/* Five across is the ceiling, same as the public pricing page: past that
          a card is narrower than the price on it, so a sixth plan wraps. */}
      <div
        className={`bp-plans${plans.length >= 5 ? ' five-up' : ''}`}
        style={{ '--plan-cols': Math.min(Math.max(plans.length, 1), 5) } as CSSProperties}
      >
        {plans.map((p) => {
          const isCurrent = p.key === effectiveKey;
          const isUpgrade = p.sort_order > effective.sort_order;
          return (
            <PlanCard key={p.key} plan={p} interval={interval}
              collapsed={phone && shownPlan !== p.key}
              onExpand={phone ? () => setOpenPlan(p.key) : undefined}
              flags={[
                ...(trialKey === p.key ? ['Your trial'] : isCurrent ? ['Your plan'] : []),
                ...(p.popular && trialKey !== p.key ? ['Most popular'] : []),
              ]}
              isCurrent={isCurrent}
              isUpgrade={isUpgrade}
              note={isCurrent
                ? (trialKey === p.key && trialEnds ? `Free until ${longDate(trialEnds)}` : 'Your current plan')
                : isUpgrade ? upgradeNote(p) : changesWhen}
              ends={!isCurrent && !isUpgrade ? endsFor(p) : undefined}
              busy={busyKey === p.key}
              onKeep={() => navigate('/admin/settings/billing')}
              onUpgrade={() => onUpgrade(p)}
              onDowngrade={() => {
                if (p.key !== targetKey) { setGateKey(p.key); }
                onDowngrade(p.key);
              }} />
          );
        })}
      </div>

      <SideBySide plans={plans} currentKey={effectiveKey} />

      {gatePlan && check && (
        <GateCard check={check} plan={gatePlan} busy={busyKey === gatePlan.key}
          when={changesWhen} ends={endsFor(gatePlan)}
          gateRef={gateRef} onMove={() => onDowngrade(gatePlan.key)} />
      )}

      {upgrading && (
        <UpgradeDialog plan={upgrading} interval={interval}
          onClose={() => setUpgrading(null)}
          onDone={() => navigate('/admin/settings/billing?checkout=upgraded')} />
      )}

      <div className="bp-how">
        <b>What happens when you switch.</b>{' '}
        <b>Moving up</b> from a paid plan changes it straight away: what&apos;s left of your current
        plan is credited against the new one for the rest of the period, the difference is charged to
        your card now, and your billing date stays the same — you see the exact amount before you
        confirm. Going from monthly to annual starts the year today, less what&apos;s left of the month;
        from annual to monthly waits for your renewal. <b>Your first paid plan</b> opens
        Stripe&apos;s own checkout{trialOffer ? `, and the first ${trialOffer.days} days are free — your card is charged on day ${trialOffer.days}` : ''}.
        Annual is one charge for the year at the plan&apos;s annual price. <b>Moving to a
        smaller plan</b> needs your usage under its limits first — the list appears above when it
        does — and changes at your renewal, or straight away when there is nothing to renew. A switch
        never deletes a form, a file or a setting; on a plan that keeps fewer submission records, the
        oldest are removed after a full export is emailed to you, and the list above says so first.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The upgrade dialog — an account that already pays moves up in place.
// ---------------------------------------------------------------------------

/** A quote line: a credit reads "−$25.00", not "$-25.00". */
const signedMoney = (cents: number, currency: string): string =>
  (cents < 0 ? `−${moneyExact(-cents, currency)}` : moneyExact(cents, currency));

/** A refusal in words: Stripe's own sentence for a decline, ours for an unconnected install. */
const upgradeRefusal = (e: unknown): string =>
  (e instanceof ApiRequestError && e.status === 503
    ? "Stripe isn't connected on this install yet — upgrades are off until it is."
    : (e as Error).message);

/** "/mo" or "/yr" for the charge after today. */
const per = (q: UpgradeQuote): string => (q.monthlyFrom || q.interval === 'month' ? 'mo' : 'yr');

/** What the move is, in one sentence, before the numbers. */
function quoteLead(q: UpgradeQuote): string {
  switch (q.kind) {
    case 'trial_switch':
      return `You move to ${q.planName} now and your free days keep running. Nothing is charged today.`;
    case 'end_free_period':
      return `Your free period ends today and ${q.planName} starts now, billed in full.`;
    case 'new_annual_term':
      return `Your annual ${q.planName} term starts today. What’s left of this month is credited against it.`;
    default:
      return q.monthlyFrom
        ? `You move to ${q.planName} now, still billed annually until your renewal, then monthly. What’s left of your current plan is credited against it.`
        : `You move to ${q.planName} now. What’s left of your current plan is credited against it for the rest of this period.`;
  }
}

/** What happens after today: the next charge and when it lands. */
function quoteAfter(q: UpgradeQuote): string | null {
  const next = q.nextAmountCents !== null ? `${money(q.nextAmountCents, q.currency)}/${per(q)}` : null;
  if (!next) return null;
  if (q.kind === 'trial_switch') return q.nextBillingAt ? `Billing starts ${longDate(q.nextBillingAt)} at ${next}.` : `Then ${next}.`;
  if (q.monthlyFrom) return `From ${longDate(q.monthlyFrom)}: ${next}.`;
  if (!q.nextBillingAt) return `Then ${next}.`;
  return q.kind === 'prorate'
    ? `Then ${next}, renewing ${longDate(q.nextBillingAt)} — your billing date stays.`
    : `Then ${next}, renewing ${longDate(q.nextBillingAt)}.`;
}

/**
 * Quote, then confirm. Every number is Stripe's own invoice preview — this
 * page does no proration arithmetic — and the charge is pinned to the
 * instant quoted. A card that will not take it leaves everything as it was,
 * and the dialog says so in Stripe's words.
 */
function UpgradeDialog({ plan, interval, onClose, onDone }: {
  plan: TenantPlan; interval: 'month' | 'year'; onClose: () => void; onDone: () => void;
}) {
  const [quote, setQuote] = useState<UpgradeQuote | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    quoteUpgrade(plan.key, interval)
      .then((r) => { if (live) setQuote(r.quote); })
      .catch((e) => { if (live) setError(upgradeRefusal(e)); });
    return () => { live = false; };
  }, [plan.key, interval]);

  async function confirm() {
    if (!quote) return;
    setBusy(true);
    setError('');
    try {
      await upgradePlan(plan.key, interval, quote.prorationDate);
      onDone();
    } catch (e) {
      setError(upgradeRefusal(e));
      setBusy(false);
    }
  }

  async function addCard() {
    setBusy(true);
    try {
      const { url } = await createPortalSession();
      window.location.assign(url);
    } catch (e) { setError((e as Error).message); setBusy(false); }
  }

  const due = quote?.dueTodayCents ?? 0;
  const after = quote ? quoteAfter(quote) : null;
  return (
    <Modal title={`Switch to ${plan.name}`} narrow onClose={onClose}
      footer={quote ? (
        <>
          {quote.needsCard
            ? <button className="fs-btn primary" disabled={busy} onClick={addCard}>Add a card</button>
            : (
              <button className="fs-btn primary" disabled={busy} onClick={confirm}>
                {due > 0 ? `Pay ${moneyExact(due, quote.currency)} and upgrade` : `Switch to ${plan.name}`}
              </button>
            )}
          <button className="fs-btn neutral" disabled={busy} onClick={onClose}>Cancel</button>
        </>
      ) : <button className="fs-btn neutral" onClick={onClose}>Close</button>}
    >
      {!quote && !error && <p className="st-muted">Asking Stripe for the exact amount…</p>}
      {quote && (
        <div className="bp-quote">
          <p className="bp-quote-lead">{quoteLead(quote)}</p>
          {quote.lines.length > 0 && (
            <table className="bp-quote-lines">
              <tbody>
                {quote.lines.map((l, i) => (
                  <tr key={i}><td>{l.description}</td><td className="num">{signedMoney(l.amountCents, quote.currency)}</td></tr>
                ))}
              </tbody>
              <tfoot>
                <tr><th scope="row">Due today</th><td className="num">{moneyExact(due, quote.currency)}</td></tr>
              </tfoot>
            </table>
          )}
          {after && <p className="bp-quote-after">{after}</p>}
          {quote.agreedPriceEndsCents !== null && (
            <p className="bp-quote-after">
              Your agreed price of {money(quote.agreedPriceEndsCents, quote.currency)} ends — {quote.planName} is
              charged at its list price.
            </p>
          )}
          {/* One discount at a time, and a plan change ends it (Ben, 2026-09-26). */}
          {quote.percentEnds && (
            <p className="bp-quote-after">
              Your {quote.percentEnds.percent}% off ends — {quote.planName} is charged at its list price.
            </p>
          )}
          {quote.needsCard
            ? (
              <div className="bl-banner bl-amber" role="status">
                <span className="bl-banner-text">
                  There’s no card on file to charge. Add one with Stripe, then come back and switch.
                </span>
              </div>
            )
            : quote.card && <p className="st-muted bp-quote-card">Charged to {cardLine(quote.card)}</p>}
        </div>
      )}
      {error && <div className="bl-banner bl-amber" role="alert"><span className="bl-banner-text">{error}</span></div>}
    </Modal>
  );
}

const gatePlanName = (plans: TenantPlan[] | null, key: string): string =>
  plans?.find((p) => p.key === key)?.name ?? key;

/** What the server says a downgrade ended, for the notice after it (a pending credit stays). */
function endedWords(ended: DiscountsEnded | null, currency?: string): string[] {
  if (!ended) return [];
  return [
    ended.agreedCents !== null ? `Your agreed price of ${money(ended.agreedCents, currency)} has ended.` : '',
    ended.percent ? `Your ${ended.percent.percent}% off has ended.` : '',
  ].filter(Boolean);
}

// ---------------------------------------------------------------------------
// One plan card.
// ---------------------------------------------------------------------------

function PlanCard({
  plan, interval, flags, isCurrent, isUpgrade, note, ends, busy, collapsed, onExpand,
  onKeep, onUpgrade, onDowngrade,
}: {
  plan: TenantPlan; interval: 'month' | 'year'; flags: string[];
  isCurrent: boolean; isUpgrade: boolean;
  /** what the button does, said before it is pressed (D7) */
  note: string;
  /** a downgrade: the discounts it ends, said before it is pressed */
  ends?: string[];
  busy: boolean;
  /** phone only: the card is a one-line header until it is tapped (§4G G6) */
  collapsed?: boolean;
  onExpand?: () => void;
  onKeep: () => void; onUpgrade: () => void; onDowngrade: () => void;
}) {
  const free = plan.price_monthly_cents === null;
  const cents = interval === 'year' ? plan.price_annual_cents : plan.price_monthly_cents;
  const saving = annualSavingCents(plan);

  const label = isCurrent ? `Keep ${plan.name}`
    : free ? 'Move to Free'
      : plan.key === 'enterprise_unlimited' ? 'Switch to Unlimited'
        : `Switch to ${plan.name}`;
  const onClick = isCurrent ? onKeep : isUpgrade ? onUpgrade : onDowngrade;

  return (
    <div
      className={`bp-plan${isCurrent ? ' current' : ''}`}
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      {/* The header repeats the name and the price, so it is rendered only
          where it is shown — a desktop keeps one "$50" on the page. */}
      {onExpand && (
        <button type="button" className="bp-plan-collapse" aria-expanded={!collapsed} onClick={onExpand}>
          {plan.name}
          <span className="price">{free ? '$0' : money(cents, plan.currency)}</span>
          <span className="caret" aria-hidden>{collapsed ? '▼' : '▲'}</span>
        </button>
      )}
      <div className="bp-plan-head">
        <div className="bp-plan-name">{plan.name}</div>
        {flags.length > 0 && (
          <div className="bp-plan-flags">{flags.map((f) => <span key={f} className="bp-plan-flag">{f}</span>)}</div>
        )}
      </div>
      {plan.tagline && <div className="bp-plan-tag">{plan.tagline}</div>}
      <div className="bp-plan-price-row">
        <span className="bp-plan-price">{free ? '$0' : money(cents, plan.currency)}</span>
        <span className="bp-plan-per">{free ? 'forever' : interval === 'year' ? '/yr' : '/mo'}</span>
      </div>
      {/* Fixed height whether or not there is a saving, so the cards line up. */}
      <div className="bp-plan-save">
        {interval === 'year' && !free && saving !== null && saving > 0
          ? `Save ${money(saving, plan.currency)} · billed up front` : ''}
      </div>
      <div className="bp-plan-lines">
        <LimitLines limits={plan.limits} />
        {/* Every plan-gated feature, one shared order and wording with the
            public pricing page — a plan without one shows it greyed, not
            hidden, so the ladder is visible from any card. */}
        {PLAN_FEATURE_ORDER.map((key) => {
          const f = featureLine(key, plan.features, plan.limits);
          const a = key === 'aiBuilder' ? assistantLine(plan.features, plan.limits) : null;
          return (
            <Fragment key={key}>
              <div className={f.has ? 'bp-feat-on' : 'bp-feat-off'}>
                {f.has ? '✓ ' : '— '}{f.text}
              </div>
              {a && (
                <div className={a.has ? 'bp-feat-on' : 'bp-feat-off'}>
                  {a.has ? '✓ ' : '— '}{a.text}
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
      <div className="bp-plan-foot">
        <button
          className={`fs-btn bp-plan-btn ${isCurrent ? 'neutral keep' : isUpgrade ? 'primary' : 'neutral'}`}
          disabled={busy} onClick={onClick}>
          {label}
        </button>
        <div className="bp-plan-note">{note}</div>
        {ends?.map((line) => <div key={line} className="bp-plan-note">{line}</div>)}
      </div>
    </div>
  );
}

function LimitLines({ limits }: { limits: PlanLimits }) {
  const n = (v: number | null, word: string, unlimitedWord = word) =>
    v === null ? <div key={word}><b>Unlimited</b> {unlimitedWord}</div> : null;
  return (
    <>
      {n(limits.forms, 'forms') ?? <div><b>{limits.forms!.toLocaleString()}</b> forms</div>}
      {n(limits.submissionsPerMonth, 'submissions')
        ?? <div><b>{limits.submissionsPerMonth!.toLocaleString()}</b> submissions a month</div>}
      {n(limits.users, 'users')
        ?? <div><b>{limits.users!.toLocaleString()}</b> user{limits.users === 1 ? '' : 's'}</div>}
      {n(limits.storageBytes, 'storage')
        ?? <div><b>{formatStorage(limits.storageBytes)}</b> storage</div>}
      {n(limits.submissionRecords, 'records kept')
        ?? <div><b>{limits.submissionRecords!.toLocaleString()}</b> records kept</div>}
      {n(limits.fieldsPerForm, 'fields and widgets per form')
        ?? <div><b>{limits.fieldsPerForm!.toLocaleString()}</b> fields and widgets per form</div>}
    </>
  );
}

// ---------------------------------------------------------------------------
// The side-by-side table — the six limits then every feature, current column tinted.
// ---------------------------------------------------------------------------

const TABLE_ROWS: { label: string; key: keyof PlanLimits }[] = [
  { label: 'Forms', key: 'forms' },
  { label: 'Submissions a month', key: 'submissionsPerMonth' },
  { label: 'Users', key: 'users' },
  { label: 'File storage', key: 'storageBytes' },
  { label: 'Submission records kept', key: 'submissionRecords' },
  { label: 'Fields and widgets per form', key: 'fieldsPerForm' },
];

const tableValue = (key: keyof PlanLimits, v: number | null): string => {
  if (v === null) return 'Unlimited';
  return key === 'storageBytes' ? formatStorage(v) : v.toLocaleString();
};

function SideBySide({ plans, currentKey }: { plans: TenantPlan[]; currentKey: string | null }) {
  const cur = (p: TenantPlan) => p.key === currentKey;
  const cell = (p: TenantPlan, extra = '') => `num bp-cell${cur(p) ? ' bp-current' : ''}${extra}`;
  return (
    <TableCard title="Side by side" className="bp-compare">
      {/* 1 + N columns never fit a phone; the card scrolls it under 900 with
          the first column stuck on solid ground. Above 900 nothing scrolls. */}
      <table className="fs-table">
        <thead>
          <tr>
            <th><span className="fs-sr-only">What</span></th>
            {plans.map((p) => (
              <th key={p.key} className={cell(p)} aria-current={cur(p) ? 'true' : undefined}>{p.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {TABLE_ROWS.map((row) => (
            <tr key={row.key}>
              <td className="bp-row-label">{row.label}</td>
              {plans.map((p) => (
                <td key={p.key} className={cell(p)}>{tableValue(row.key, p.limits[row.key])}</td>
              ))}
            </tr>
          ))}
          {/* Feature rows in the shared order the pricing page uses. The AI
              row carries its monthly allowance rather than a bare tick — the
              number is what differs per column. */}
          {PLAN_FEATURE_ORDER.map((key) => (
            <Fragment key={key}>
              <tr>
                <td className="bp-row-label">{PLAN_FEATURE_LINE[key]}</td>
                {plans.map((p) => {
                  const f = featureLine(key, p.features, p.limits);
                  const value = key === 'aiBuilder'
                    ? (aiAllowance(p.features, p.limits) ?? '—')
                    : f.has ? '✓' : '—';
                  return <td key={p.key} className={cell(p, f.has ? ' has' : ' off')}>{value}</td>;
                })}
              </tr>
              {/* The assistant's own allowance, under the builder it rides on. */}
              {key === 'aiBuilder' && (
                <tr>
                  <td className="bp-row-label">{AI_ASSISTANT_LINE}</td>
                  {plans.map((p) => {
                    const allowance = assistantAllowance(p.features, p.limits);
                    return <td key={p.key} className={cell(p, allowance ? ' has' : ' off')}>{allowance ?? '—'}</td>;
                  })}
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </TableCard>
  );
}

// ---------------------------------------------------------------------------
// The downgrade checklist card — a to-do list, not a wall.
// ---------------------------------------------------------------------------

const GATE_LABEL: Record<DowngradeCheckItem['limit'], string> = {
  storageBytes: 'File storage',
  forms: 'Forms',
  users: 'Users',
  sso: 'SSO',
  submissionRecords: 'Submission records',
  customDomains: 'Custom domains',
};

/** Red for the physically blocking, amber for the reducible, grey for the informational. */
const gateTone = (i: DowngradeCheckItem): string => {
  if (!i.blocking || i.resolved) return '';
  return i.action === 'delete_files' ? 'danger' : 'warn';
};

const gateUsed = (i: DowngradeCheckItem): string => {
  if (i.limit === 'storageBytes') return formatStorage(i.used);
  if (i.limit === 'sso') return i.used > 0 ? `on ${i.used.toLocaleString()} form${i.used === 1 ? '' : 's'}` : '—';
  if (i.limit === 'customDomains') return i.used > 0 ? i.used.toLocaleString() : '—';
  return i.used.toLocaleString();
};

const gateAllowed = (i: DowngradeCheckItem): string => {
  if (i.limit === 'sso' || i.limit === 'customDomains') return i.allowed ? '✓' : '—';
  if (i.allowed === null) return 'Unlimited';
  return i.limit === 'storageBytes' ? formatStorage(i.allowed) : i.allowed.toLocaleString();
};

function GateAction({ item }: { item: DowngradeCheckItem }) {
  switch (item.action) {
    case 'delete_files':
      return <Link className="fs-btn sm neutral" to="/admin/settings/billing#storage">Review files</Link>;
    case 'disable_forms':
      return <Link className="fs-btn sm neutral" to="/admin">Choose forms</Link>;
    case 'remove_users':
      return <Link className="fs-btn sm neutral" to="/admin/settings/users">Manage users</Link>;
    default:
      return item.limit === 'submissionRecords'
        ? <Link className="fs-btn sm neutral" to="/admin">Export now</Link>
        : <span className="st-muted bp-nothing">nothing to do</span>;
  }
}

function GateCard({ check, plan, busy, when, ends, gateRef, onMove }: {
  check: DowngradeCheck; plan: TenantPlan; busy: boolean;
  /** "Changes on 1 October 2026" — when the move lands once it is allowed */
  when: string;
  /** the discounts the move ends, said before it is pressed */
  ends: string[];
  gateRef: React.MutableRefObject<HTMLDivElement | null>; onMove: () => void;
}) {
  /** a list rather than five columns (§4G G6) */
  const phone = useMediaQuery(PHONE);
  const unresolved = check.items.filter((i) => i.blocking && !i.resolved);
  const names = unresolved.map((i) => GATE_LABEL[i.limit].toLowerCase());
  const nameList = names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names[0] || '';

  return (
    <section className="fs-card bp-gate" ref={gateRef} aria-labelledby="bp-gate-title">
      <div className="bp-gate-head">
        <h2 id="bp-gate-title">Before you can move to {plan.name}</h2>
        <span className="fs-pill warn">
          {unresolved.length === 0 ? 'nothing left to clear'
            : `${unresolved.length} thing${unresolved.length === 1 ? '' : 's'} to clear`}
        </span>
      </div>
      <p className="bp-gate-intro">
        A smaller plan has to be able to hold what you have, so we ask you to come under its limits
        before the change rather than after. Nothing on this list deletes a submission.
      </p>
      {phone ? (
        <div className="bp-gate-list">
          {check.items.map((i) => (
            <div className="bp-gate-item" key={i.limit}>
              <div className="what">
                <span className={`bp-dot ${gateTone(i)}`} />
                {GATE_LABEL[i.limit]} — {gateUsed(i)} used, {plan.name} allows {gateAllowed(i)}
              </div>
              <div className="says">{i.detail}</div>
              <span className="go"><GateAction item={i} /></span>
            </div>
          ))}
        </div>
      ) : (
        // The table card's wrapper: it scrolls inside the card under 900 (never
        // clipping the Fix-it buttons between 600 and 680), inert above.
        <div className="fs-tablecard-scroll">
        <table className="fs-table bp-gate-table">
          <thead>
            <tr>
              <th>What</th>
              <th className="num">You have</th>
              <th className="num">{plan.name} allows</th>
              <th>What happens</th>
              <th className="fs-row-actions"><span className="fs-sr-only">Fix it</span></th>
            </tr>
          </thead>
          <tbody>
            {check.items.map((i) => {
              const open = i.blocking && !i.resolved;
              return (
                <tr key={i.limit}>
                  <td><span className="bp-gate-what"><span className={`bp-dot ${gateTone(i)}`} /><b>{GATE_LABEL[i.limit]}</b></span></td>
                  <td className={`num${open ? ` bp-over ${gateTone(i)}` : ' st-muted'}`}>{gateUsed(i)}</td>
                  <td className="num">{gateAllowed(i)}</td>
                  <td className={open ? 'bp-gate-says open' : 'bp-gate-says'}>{i.detail}</td>
                  <td className="fs-row-actions"><GateAction item={i} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
      <div className="bp-gate-foot">
        <button className="fs-btn primary" disabled={check.blocked || busy} onClick={onMove}>
          Move to {plan.name}
        </button>
        <span className="st-muted">
          {check.blocked
            ? `Available once ${nameList} ${unresolved.length === 1 ? 'is' : 'are'} under the ${plan.name} limits. We re-check when you come back to this page.`
            : `${when}. Nothing is deleted.`}
        </span>
        {ends.map((line) => <span key={line} className="st-muted">{line}</span>)}
      </div>
    </section>
  );
}
